import { useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { format } from "date-fns";
import {
  ArticleStatus,
  ArticleCategory,
  type PublicArticle,
} from "@vedaunion/shared";
import { useArticles, useDuplicateArticle } from "../../lib/articles";
import { useCan } from "../../lib/auth";
import { ApiError } from "../../lib/api";
import { useNavigate } from "react-router-dom";

/**
 * Authenticated articles index. Three sub-tabs (driven by the `view` URL
 * search param): My articles (anyone with articles.create) — All articles
 * (only editors/admins) — Published news (everyone, mirrors /news). Filter
 * chips narrow by status, category, and "featured only".
 */

type View = "mine" | "all" | "news";

const STATUS_OPTIONS = ArticleStatus.options;
const CATEGORY_OPTIONS = ArticleCategory.options;

export default function ArticlesList() {
  const canCreate = useCan("articles.create");
  const canEditAny = useCan("articles.edit_any");
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  const availableViews: View[] = useMemo(() => {
    const v: View[] = [];
    if (canCreate) v.push("mine");
    if (canEditAny) v.push("all");
    v.push("news");
    return v;
  }, [canCreate, canEditAny]);

  const requestedView = params.get("view") as View | null;
  const view: View =
    requestedView && availableViews.includes(requestedView) ? requestedView : availableViews[0]!;

  const status = params.get("status") ?? "";
  const category = params.get("category") ?? "";
  const featured = params.get("featured") === "1";

  const setParam = (key: string, value: string | null) => {
    const next = new URLSearchParams(params);
    if (value === null || value === "") next.delete(key);
    else next.set(key, value);
    setParams(next, { replace: true });
  };

  // Status filter only applies to mine/all; the news view is implicitly published.
  const apiParams = useMemo(() => {
    const base = {
      limit: 100,
      ...(category ? { category } : {}),
      ...(featured ? { featured: true } : {}),
    } as const;
    if (view === "news") {
      // Default list endpoint = published & public. No status override.
      return base;
    }
    return {
      ...base,
      ...(view === "mine" ? { mine: true } : { all: true }),
      ...(status ? { status } : {}),
    } as const;
  }, [view, status, category, featured]);

  const articlesQ = useArticles(apiParams);
  const articles = articlesQ.data?.articles ?? [];
  const isLoading = articlesQ.isLoading;

  const duplicate = useDuplicateArticle();
  const onDuplicate = async (id: number) => {
    try {
      const out = await duplicate.mutateAsync(id);
      navigate(`/dashboard/articles/${out.article.slug}/edit`);
    } catch (err) {
      const e = err as ApiError;
      window.alert(e?.body?.message ?? e?.message ?? "Duplicate failed.");
    }
  };

  const showAuthor = view !== "mine";
  const isEditableView = view !== "news";

  return (
    <div className="container-edit">
      <header className="flex items-baseline justify-between gap-3 flex-wrap mb-6">
        <div>
          <p className="page-eyebrow mb-1.5">Articles</p>
          <h1 className="page-title">Words on the page.</h1>
        </div>
        {canCreate && (
          <Link to="/dashboard/articles/new" className="btn-app">
            New article
          </Link>
        )}
      </header>

      {/* Sub-tabs */}
      <nav className="flex flex-wrap gap-x-4 gap-y-2 border-b border-[var(--color-rule)] mb-5" aria-label="Article views">
        {availableViews.map((v) => (
          <button
            key={v}
            type="button"
            className={`subtab ${view === v ? "is-active" : ""}`}
            onClick={() => setParam("view", v === availableViews[0] ? null : v)}
          >
            {viewLabel(v)}
          </button>
        ))}
      </nav>

      {/* Filter chips */}
      <div className="flex flex-wrap items-center gap-2 mb-7">
        {isEditableView && (
          <FilterChip
            label="Status"
            value={status}
            options={STATUS_OPTIONS as readonly string[]}
            onChange={(v) => setParam("status", v)}
          />
        )}
        <FilterChip
          label="Category"
          value={category}
          options={CATEGORY_OPTIONS as readonly string[]}
          onChange={(v) => setParam("category", v)}
        />
        <button
          type="button"
          className={`chip ${featured ? "is-active" : ""}`}
          onClick={() => setParam("featured", featured ? null : "1")}
        >
          Featured only
        </button>
        {(status || category || featured) && (
          <button
            type="button"
            className="chip chip-clear"
            onClick={() => {
              const next = new URLSearchParams(params);
              next.delete("status");
              next.delete("category");
              next.delete("featured");
              setParams(next, { replace: true });
            }}
          >
            Clear filters
          </button>
        )}
      </div>

      {isLoading ? (
        <p className="text-[var(--color-ink-mute)]">Loading…</p>
      ) : articles.length === 0 ? (
        <EmptyState view={view} canCreate={canCreate} />
      ) : (
        <ul className="flex flex-col">
          {articles.map((a) => (
            <ArticleRow
              key={a.id}
              a={a}
              showAuthor={showAuthor}
              showEdit={isEditableView}
              onDuplicate={canCreate ? () => onDuplicate(a.id) : undefined}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

function viewLabel(v: View): string {
  if (v === "mine") return "My articles";
  if (v === "all") return "All articles";
  return "Published news";
}

function FilterChip({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: readonly string[];
  onChange: (v: string) => void;
}) {
  return (
    <label className={`chip chip-select ${value ? "is-active" : ""}`}>
      <span className="chip-label">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-label={label}
      >
        <option value="">all</option>
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    </label>
  );
}

function EmptyState({ view, canCreate }: { view: View; canCreate: boolean }) {
  const title =
    view === "news"
      ? "Nothing published yet."
      : view === "all"
        ? "No articles match these filters."
        : "Nothing of yours yet.";
  const body =
    view === "news"
      ? "Published articles surface here and on the public /news page."
      : view === "all"
        ? "Try clearing the filters or switch to a different view."
        : "Begin a new piece — you'll find a Blank canvas plus templates for editorials, verse studies, news, longform, recaps, and a few quieter formats.";
  return (
    <div className="veda-empty">
      <p className="font-grotesk text-[0.85rem] text-[var(--color-ink-soft)] mb-1.5">{title}</p>
      <p className="text-[0.95rem] text-[var(--color-ink-soft)] max-w-[52ch] mx-auto leading-[1.55]">
        {body}
      </p>
      {canCreate && view !== "news" && (
        <Link to="/dashboard/articles/new" className="btn-app mt-5 inline-flex">
          Begin a new piece
        </Link>
      )}
    </div>
  );
}

function ArticleRow({
  a,
  showAuthor,
  showEdit,
  onDuplicate,
}: {
  a: PublicArticle;
  showAuthor: boolean;
  showEdit: boolean;
  onDuplicate?: () => void;
}) {
  const stamp = a.publishedAt ?? a.updatedAt;
  return (
    <li className="grid grid-cols-1 sm:grid-cols-[180px_1fr_140px] gap-2 sm:gap-6 py-5 border-t border-[var(--color-rule)] first:border-t-0 items-baseline">
      <p className="font-grotesk text-[0.7rem] tracking-[0.14em] uppercase text-[var(--color-ink-mute)]">
        {format(new Date(stamp), "d LLL yyyy")}
        <span className="mx-2">·</span>
        <span className={a.status === "published" ? "text-[var(--color-violet)]" : ""}>
          {a.status}
        </span>
        {a.featured && (
          <>
            <span className="mx-2">·</span>
            <span className="text-gold">featured</span>
          </>
        )}
      </p>
      <div className="flex gap-3 items-start min-w-0">
        {a.coverImage && (
          <img
            src={a.coverImage}
            alt=""
            className="shrink-0 w-12 h-12 object-cover mt-0.5 hidden sm:block"
          />
        )}
        <div className="min-w-0">
          <Link
            to={showEdit ? `/dashboard/articles/${a.slug}` : `/news/${a.slug}`}
            className="font-display text-[1.4rem] sm:text-[1.5rem] leading-[1.18] tracking-tight hover:text-[var(--color-violet)] transition-colors"
          >
            {a.title}
          </Link>
          {a.subtitle && (
            <p className="font-display-italic text-[1rem] text-[var(--color-ink-mute)] mt-1">
              {a.subtitle}
            </p>
          )}
          {a.excerpt && (
            <p className="text-[0.9rem] leading-[1.55] text-[var(--color-ink-soft)] mt-1.5 max-w-[60ch]">
              {a.excerpt}
            </p>
          )}
          {showAuthor && a.authorName && (
            <p className="font-grotesk text-[0.66rem] tracking-[0.16em] uppercase text-[var(--color-ink-mute)] mt-2">
              By {a.authorName}
            </p>
          )}
        </div>
      </div>
      <div className="flex flex-wrap gap-3 items-baseline justify-start sm:justify-end">
        {showEdit && (
          <Link
            to={`/dashboard/articles/${a.slug}/edit`}
            className="row-action"
          >
            Edit
          </Link>
        )}
        {!showEdit && (
          <Link
            to={`/news/${a.slug}`}
            className="row-action"
          >
            Read
          </Link>
        )}
        {onDuplicate && (
          <button type="button" onClick={onDuplicate} className="row-action">
            Duplicate
          </button>
        )}
      </div>
    </li>
  );
}
